import React, { useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import clsx from 'classnames';
import { OrderCardWithButton as UIOrderCardWithButton } from '@shopee/chatbot-ui';
import { BannerType, FoodOrderServiceType } from '@shopee/chatbot-ui/lib/components/FoodOrder';
import { OrderCardType } from '@shopee/chatbot-ui/lib/components/OrderList';
import { ButtonProps } from '@shopee/chatbot-ui/lib/components/Button';
import { OrderType } from '@shopee/chatbot-ui/lib/components/OrderCardWithButton';

import { GeneralButton } from '@/interfaces';
import { InitStoreType } from '@/types/store/init';
import { getChildInject, getTrans, bridgeCallFunc } from '@/utils/instantLoaded/tools';
import { cdnFile } from '@/utils/cdnFile';
import { formatPrice, getContent } from '@/utils/tools';
import {
  ButtonAction,
  ButtonActionV1,
  ButtonTypeV1,
  getButtonAction,
  getButtonActionV1,
  getButtonDataV1,
  getButtonTypeV1,
} from '@/utils/chatuiAdapters';
import {
  ORDER_STATUS_TRACKING_MAP,
  RR_STATUS_TRACKING_MAP,
  TRACKING_SECTION,
  TRACKING_TARGET,
  TRACKING_TYPE,
} from '@/utils/tracking/constants';
import {
  TRACKING_MSG_TYPE,
  CUSTOMER_HIDDEN_TEXT,
  dirverOrderStatusText,
  SELECT_ORDER_TEXT,
  BUYER_CANCELLED,
  YOU_MAY_WANT_TO_ASK,
} from '@/utils/constants';
import { oldTaskbotLoadMore, useForbidOrderRedirect, All_BUYER_TITLES } from '../PropsProtocol/OrderList';
import { IntentSource, IntentType, OtherFrequentlyAsked } from '@/types/intent';
import agentStore from '@/stores/agent';
import { addDialogue, updateDialogueInfo } from '@/utils/sessionUtils';
import { isMinimiseCategoryOrderSpecificEntryPoint } from '@/utils/experiment/getMinimiseCategory';

import HotQuestionUIOptimisationItem from '../HotQuestionUIOptimisationItem';
import getOrderMilestone from './getOrderMilestone';
import { getDriverOrderStatusTrans } from '../PropsProtocol/DriverOrderListPopup';
import { isDriverAndFoodUIOptimisation } from '@/utils/experiment/getDriverAndFoodUIOptimisationExperiment';
import { isDriverVN } from '@/config/core';

export enum RepeatedSessionType {
  CheckRR = 'check_rr',
  Order = 'order',
}

const FROM_ORDER_SHORTCUT = 1;
const NOT_FROM_ORDER_SHORTCUT = 0;

export interface ShopeeOrderInfo {
  return_items_count: number;
  refund_amount: number;
  return_status: number;
  return_status_desc: string;
  order_status_desc: string;
  total_price: number;
  order_id: string;
  order_status: number;
  order_status_text: number;
  order_sn: string;
  shop_id: number;
  checkout_id: number;
  uri_key: string;
  title: string;
  image: string;
  price: number;
  specification: string;
  button_type: string;
  button_id: string;
  tracing_number: string;
  delivered_by: string;
  item_count: number;
  cancel_time_str: string;
  delivery_time_str: string;
  expected_receive_time_str: string;
  ship_by_date_str: string;
  milestone?: number;
}

interface FoodOrderInfo {
  delivery_id: number;
  pickup_seq: number;
  delivery_status: string;
  shop_name: string;
  order_id: number;
  receiver_name: string;
  delivery_location: { address: string };
  order_id_string: string;
  reference_order_id: string;
  short_code: string;
  service_type: FoodOrderServiceType;
  pickup_location: {
    address: string;
  }
}

enum FromType {
  Message = 1,
}

export type OrderCardWithButtonProps = {
  nodePointId: string;
  dialogueId: string;
  dialogueType: string;
  isHistory: boolean;
  listPos: number;
  isDisabled: boolean;
  kbBotId: number;
  kbBotName: string;

  historyDialogueId?: string;
  historySessionId?: string;

  order: ShopeeOrderInfo | FoodOrderInfo;
  buttons: GeneralButton[];
  intents: any[];
  repeatedSessionType: RepeatedSessionType;
  orderType?: OrderType;
  otherFrequentlyAsked?: OtherFrequentlyAsked;

  initStore?: InitStoreType;
  shouldDisplayInstantly: boolean;
  sessionId: string;
  msgContent?: string;
  fromType?: FromType;
  separateMessage?: boolean;
  triggeredByOrderShortcut?: boolean;
  cardTitle?: string;
  useUIOptimisationHotQuestion?: boolean;
  enableMinimiseCategoryUI?: boolean;
};

const OrderCardWithButton: React.FC<OrderCardWithButtonProps> = (props) => {
  const {
    order,
    buttons,
    intents,
    isHistory,
    historySessionId,
    initStore,
    kbBotId,
    kbBotName,
    repeatedSessionType,
    orderType = OrderType.shopee,
    historyDialogueId,
    sessionId,
    dialogueId,
    dialogueType,
    nodePointId,
    fromType,
    separateMessage,
    triggeredByOrderShortcut,
    cardTitle,
    useUIOptimisationHotQuestion,
    otherFrequentlyAsked,
    enableMinimiseCategoryUI,
  } = props;
  const isOrderRepeatedSession = repeatedSessionType === RepeatedSessionType.Order;
  const isMinimiseCategoryUI = (enableMinimiseCategoryUI && isMinimiseCategoryOrderSpecificEntryPoint()) || isDriverAndFoodUIOptimisation();

  const getTrackingDataSectionAndTarget = () => {
    switch (orderType) {
      case OrderType.shopee:
        return {
          trackingname: TRACKING_TYPE.REPEATED_SESSION_ORDER_CARD_BUBBLE,
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.REPEATED_SESSION_ORDER_CARD_BUBBLE],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.REPEATED_SESSION_ORDER_CARD_BUBBLE],
        };

      case OrderType.food:
      case OrderType.orderWithHotQuestion:
        return {
          trackingname: TRACKING_TYPE.HOTQUESTION,
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.HOTQUESTION],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.HOTQUESTION],
        };

      case OrderType.prediction:
        return {
          trackingname: TRACKING_TYPE.ORDER_CARD_HOT_QUESTION,
          trackingsection: TRACKING_SECTION[TRACKING_TYPE.ORDER_CARD_HOT_QUESTION],
          trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER_CARD_HOT_QUESTION],
        };
    }
  };

  const getTrackingStatus = () => {
    const { order_status_desc, return_status_desc, order_status_text } = (order as ShopeeOrderInfo) || {};

    let orderStatus = ORDER_STATUS_TRACKING_MAP[order_status_text as keyof typeof ORDER_STATUS_TRACKING_MAP] ?? '';

    if (orderType === OrderType.prediction) {
      orderStatus = isOrderRepeatedSession ? order_status_desc : return_status_desc || order_status_desc;
    }

    return orderStatus;
  };

  const getTrackingDataBasic = (button: GeneralButton, index: number) => {
    if (!button.button_type) {
      return {};
    }

    const getButtonTrackingData: {
      [key in ButtonActionV1]?: () => any;
    } = {
      [ButtonActionV1.KB_INTENT]: () => ({
        chat_session_id: isHistory ? historySessionId : initStore?.sessionId || '',
        chat_dialogue_id: isHistory ? historyDialogueId : dialogueId || '0',
        if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        question_content: getContent(button),
        rr_status:
          RR_STATUS_TRACKING_MAP[(order as ShopeeOrderInfo).return_status as keyof typeof RR_STATUS_TRACKING_MAP] ?? '',
        bot_name: kbBotName,
        is_order_card: true,
        is_order_shortcut: triggeredByOrderShortcut ? FROM_ORDER_SHORTCUT : NOT_FROM_ORDER_SHORTCUT,
        if_ds_predicted: 'false',
        order_status: getTrackingStatus(),
        bot_id: kbBotId,
        location: index,
        question_id: button.kb_intent_id,
        kb_intent_id: button.kb_intent_id,
        is_order_predicted: fromType === FromType.Message ? 'false' : 'true',
        order_id: (order as FoodOrderInfo)?.order_id_string || order.order_id,
      }),
    };

    return getButtonTrackingData[button.button_type as ButtonActionV1]?.() || {};
  };

  const formatButtons = buttons?.map((btn, index) => {
    const text = getContent(btn);
    const getStyle = () => {
      if (useUIOptimisationHotQuestion) {
        return {};
      }
      return orderType === OrderType.shopee
        ? {}
        : orderType === OrderType.food
          ? {
            display: 'block',
            color: '#0088FF',
            textAlign: 'left',
            ...isDriverAndFoodUIOptimisation() ? {
              padding: index ? '14px 12px 14px 0' : '14px 12px',
              marginLeft: index ? '12px' : '0',
            } : {
              padding: '14px 0',
              marginLeft: '12px',
            },
            fontSize: '1em',
          }
          : {
            display: 'block',
            color: props.isHistory ? 'rgba(0,0,0,.26)' : '#0088FF',
            textAlign: 'left',
            padding: index ? '14px 12px 14px 0' : '14px 12px',
            marginLeft: index ? '12px' : '0',
            fontSize: '16px',
          };
    };
    return {
      text,
      type: getButtonTypeV1(btn.style as ButtonTypeV1),
      style: getStyle(),
      action: {
        func: getButtonActionV1(btn.button_type as ButtonActionV1),
        ...getButtonDataV1(btn),
      },
      disabled: !!props.isHistory,
      trackingData: {
        ...getTrackingDataSectionAndTarget(),
        trackingdataBasic: getTrackingDataBasic(btn, index),
      },
      ...HotQuestionUIOptimisationItem(text, useUIOptimisationHotQuestion, { isMinimiseCategoryUI }),
    };
  });

  const formatIntentToButtons = intents?.map((intent, index) => {
    const trackingdataBasic = {
      content: intent.hotquestion_text,
      question_content: intent.hotquestion_text,
      chat_session_id: isHistory ? historySessionId : sessionId,
      question_id: intent.intent_id,
      is_order_card: true,
      if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
      is_ds_predicted: intent.intent_type === IntentType.Prediction,
      intent_source: intent.intent_source,
      kb_intent_id: intent.intent_source === IntentSource.KnowledgeBase ? intent.intent_id : undefined,
      bot_id: kbBotId,
      bot_name: kbBotName,
      question_category: 'initial',
      location: index,
      is_faq: false,
      is_liveAgentRecommender: 0,
      chat_dialogue_id: isHistory ? historyDialogueId : dialogueId || '0',
      user_type: agentStore.userTypeName,
      agent_enquiry_category: agentStore.enquiryTypeName,
      is_repeated_session: intent.is_repeated_session ? 1 : 0,
      rr_status: return_status_desc,
      order_status: order_status_desc,
    };
    const text = intent.hotquestion_text;
    const trackingData = {
      ...getTrackingDataSectionAndTarget(),
      bypassClickTracking: true,
      trackingdataBasic,
    };

    return {
      button_id: intent.intent_id,
      text,
      type: 'link',
      style: useUIOptimisationHotQuestion
        ? {}
        : {
          display: 'block',
          color: props.isHistory ? 'rgba(0,0,0,.26)' : '#0088FF',
          textAlign: 'left',
          padding: index ? '14px 12px 14px 0' : '14px 12px',
          marginLeft: index ? '12px' : '0',
          fontSize: '16px',
        },
      action: {
        func: getButtonAction(ButtonAction.ClickIntent),
        info: { trackingData },
        nextParams: intent.query_callback,
        ...intent,
      },
      disabled: !!isHistory,
      // 取消button本身的click事件上报，在uiEvent clickIntent事件结束后获取next_dialogue_id再上报
      trackingData,
      ...HotQuestionUIOptimisationItem(text, useUIOptimisationHotQuestion, { isMinimiseCategoryUI }),
    };
  });

  const otherFrequentlyAskedButton = otherFrequentlyAsked
    ? {
      text: getContent(otherFrequentlyAsked),
      type: 'link',
      action: {
        func: getButtonAction(ButtonAction.JumpNode),
        nextParams: otherFrequentlyAsked.query_callback,
      },
      disabled: !!isHistory,
      trackingData: {
        trackingname: TRACKING_TYPE.CATEGORY_OTHER_FREQUENTLY_ASKED,
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.CATEGORY_OTHER_FREQUENTLY_ASKED],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.CATEGORY_OTHER_FREQUENTLY_ASKED],
        trackingdataBasic: {
          chat_session_id: isHistory ? historySessionId : initStore?.sessionId || '',
          chat_dialogue_id: isHistory ? historyDialogueId : dialogueId || '0',
          location: (orderType === OrderType.orderWithHotQuestion
            ? (formatIntentToButtons as ButtonProps[])
            : (formatButtons as ButtonProps[]) ?? []).length,
        },
      },
      ...HotQuestionUIOptimisationItem(getContent(otherFrequentlyAsked), useUIOptimisationHotQuestion, {
        isMinimiseCategoryUI,
        adhocCategoryName: otherFrequentlyAsked?.adhoc_category_name?.[0],
        isOtherFrequentlyAsked: true,
      }),
    }
    : null;

  otherFrequentlyAskedButton && formatButtons?.push(otherFrequentlyAskedButton as any);
  otherFrequentlyAskedButton && formatIntentToButtons?.push(otherFrequentlyAskedButton as any);

  let orderProp = null;

  const {
    image,
    total_price,
    refund_amount,
    title,
    item_count,
    return_items_count,
    order_status_desc,
    return_status_desc,
    delivered_by,
    order_status,
    cancel_time_str,
    delivery_time_str,
    expected_receive_time_str,
    ship_by_date_str,
    milestone,
  } = (order as ShopeeOrderInfo) || {};

  const defaultOrderProps = {
    isUIOptimisationHotQuestion: useUIOptimisationHotQuestion,
    useUIOptimisationHotQuestion,
    milestone: getOrderMilestone({
      useUIOptimisationHotQuestion,
      milestone: 0, // 这个默认就是 不展示 milestone 那些所以给个 0
      order,
    }),
  };
  switch (orderType) {
    case OrderType.food:
      break;
  }

  switch (orderType) {
    case OrderType.shopee:
      orderProp = {
        ...defaultOrderProps,
        orderId: order.order_id,
        orderCardType: OrderCardType.newCarousel,
        image: cdnFile(image),
        price: isOrderRepeatedSession ? formatPrice(total_price) : formatPrice(refund_amount || total_price),
        totalPrice: isOrderRepeatedSession ? formatPrice(total_price) : formatPrice(refund_amount || total_price),
        title: title,
        itemCount: isOrderRepeatedSession ? item_count : return_items_count,
        orderStatusText: isOrderRepeatedSession ? order_status_desc : return_status_desc || order_status_desc,
        showTotalText: false,
      };
      break;

    case OrderType.prediction:
      const copyTracingNumber = (value: string | number) => {
        const params = { text: String(value) };
        bridgeCallFunc(() => {
          window.bridgeCallHandler('copyTextToClipboard', params);
        });
      };

      orderProp = {
        ...defaultOrderProps,
        orderId: order.order_id,
        orderSn: (order as ShopeeOrderInfo).order_sn,
        orderCardType: OrderCardType.orderWithCopy,
        image: cdnFile(image),
        price: isOrderRepeatedSession ? formatPrice(total_price) : formatPrice(refund_amount || total_price),
        totalPrice: isOrderRepeatedSession ? formatPrice(total_price) : formatPrice(refund_amount || total_price),
        title: title,
        itemCount: item_count,
        // itemCount: isOrderRepeatedSession ? item_count : return_items_count,
        orderStatusText: isOrderRepeatedSession ? order_status_desc : return_status_desc || order_status_desc,
        showTotalText: true,
        deliverer: delivered_by,
        handleCopyClick: () => copyTracingNumber((order as ShopeeOrderInfo).order_sn),
      };
      break;

    case OrderType.food:
      const { order_id, delivery_status, shop_name, delivery_location, pickup_seq, service_type, pickup_location } = order as FoodOrderInfo;
      const hideBuyerInfoStatus = ['Delivered', 'Cancelled'];
      const buyerInfo = hideBuyerInfoStatus.includes(delivery_status)
        ? getTrans(CUSTOMER_HIDDEN_TEXT)
        : `${delivery_location?.address}`;

      orderProp = {
        ...defaultOrderProps,
        orderId: order_id,
        bannerType: BannerType.OrderCode,
        status: getDriverOrderStatusTrans(delivery_status as keyof typeof dirverOrderStatusText),
        shopName: service_type === FoodOrderServiceType.Food ? shop_name : pickup_location?.address,
        buyerInfo,
        orderCode: getDriverOrderCode(order as FoodOrderInfo),
        serviceType: service_type,
      };
      break;

    case OrderType.orderWithHotQuestion:
      orderProp = {
        ...defaultOrderProps,
        orderId: order.order_id,
        orderCardType: OrderCardType.newCarousel,
        image: cdnFile(image),
        price: formatPrice(total_price),
        totalPrice: formatPrice(total_price),
        title: title,
        itemCount: item_count,
        orderStatusText: order_status_desc,
        showTotalText: false,
        showOneLineTitle: true,
        createdTime:
          order_status === BUYER_CANCELLED
            ? cancel_time_str
            : delivery_time_str || expected_receive_time_str || ship_by_date_str,
      };
      break;
  }

  const onLoadMore = (loadMoreProps: any) => {
    const { sessionId, dialogueId } = props;

    return oldTaskbotLoadMore(
      loadMoreProps,
      { is_seller: false, sessionId, dialogueId },
      useForbidOrderRedirect(false),
      { orderType, orderCardType: OrderCardType.smallOrder },
    );
  };

  const trackingProps = {
    componentTrackingData: {
      moreOrder: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.ORDER_CARD_OTHER_ORDER],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.ORDER_CARD_OTHER_ORDER],
        trackingdataBasic: {
          chat_session_id: props.isHistory ? props.historySessionId : props.sessionId || '',
          chat_dialogue_id: props.isHistory ? props.historyDialogueId : props.dialogueId,
          if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
          order_id: props.order.order_id,
          order_status: getTrackingStatus(),
          is_order_predicted: fromType === FromType.Message ? 'false' : 'true',
        },
      },
      panelOrder: {
        trackingsection: TRACKING_SECTION.panelOrder,
        trackingtarget: TRACKING_TARGET.panelOrder,
        trackingdataBasic: {
          chat_session_id: isHistory ? historySessionId : sessionId,
          chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
          node_point_id: nodePointId,
          origin: 'List',
          button_name: getTrans(SELECT_ORDER_TEXT) || '',
          button_text: getTrans(SELECT_ORDER_TEXT),
          if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
        trackingItemData: (data: any) => ({
          order_status_tab: data?.orderStatusText,
          orderid: data?.orderId,
          order_status: data?.orderStatusText,
        }),
      },
      panelOrderButton: {
        trackingsection: TRACKING_SECTION.orderSelection,
        trackingtarget: TRACKING_TARGET.button,
        trackingdataBasic: {
          chat_session_id: sessionId || '0',
          chat_dialogue_id: dialogueId || '',
          origin: 'List',
          button_name: getTrans(SELECT_ORDER_TEXT),
        },
        trackingItemData: (data: any) => ({ order_status_tab: data?.orderStatusText }),
      },
      closePanel: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_CLOSE_PANEL],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_CLOSE_PANEL],
        trackingdataBasic: {
          chat_session_id: isHistory ? historySessionId : sessionId,
          chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
          node_point_id: nodePointId,
          if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
      },
      panelOrderScroll: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL_PANEL],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_SCROLL_PANEL],
        trackingdataBasic: {
          chat_session_id: isHistory ? historySessionId : sessionId,
          chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
          node_point_id: nodePointId,
          if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
        trackingItemData: (data: any) => ({ tab_name: data?.name }),
      },
      tab: {
        trackingsection: TRACKING_SECTION[TRACKING_TYPE.TFE_ORDER_LIST_TAB],
        trackingtarget: TRACKING_TARGET[TRACKING_TYPE.TFE_ORDER_LIST_TAB],
        trackingdataBasic: {
          chat_session_id: isHistory ? historySessionId : sessionId,
          chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
          node_point_id: nodePointId,
          if_history: isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
        },
        trackingItemData: (data: any) => ({ tab_name: data?.title?.name }),
      },
    },
  };

  useEffect(() => {
    if (separateMessage && !props.isHistory && props.shouldDisplayInstantly) {
      addDialogue(
        {
          dialogueId: dialogueId,
          dialogueType: dialogueType,
          from: 'bot',
          type: 'TipMessage',
          data: { content: separateMessage },
        },
        false,
      );

      props.listPos && updateDialogueInfo({ shouldDisplayInstantly: false }, props.listPos);
    }
  }, []);

  return (
    <>
      {/* 这里这么写是为了 不出现多个头部 */}
      {useUIOptimisationHotQuestion && orderType !== OrderType.prediction ? (
        <div
          className={clsx('order-card-title-ui-optimisation-hot-question', { 'minimise-category-ui-title': isMinimiseCategoryUI })}
        >
          {getTrans(YOU_MAY_WANT_TO_ASK) || 'You may want to ask:'}
        </div>
      ) : null}
      <UIOrderCardWithButton
        {...trackingProps}
        className={clsx({
          'ui-optimisation-hot-question-container': useUIOptimisationHotQuestion,
          'minimise-category-ui': isMinimiseCategoryUI,
        })}
        disabled={props.isHistory}
        title={cardTitle || ''}
        tabs={All_BUYER_TITLES}
        defaultSelected={0}
        orderType={orderType}
        onLoadMore={onLoadMore as any}
        showMoreButton={triggeredByOrderShortcut ? false : true}
        order={orderProp as any}
        buttons={{
          buttons:
            orderType === OrderType.orderWithHotQuestion
              ? (formatIntentToButtons as ButtonProps[])
              : (formatButtons as ButtonProps[]),
        }}
        // 只有Food和driver的订单卡片需要优化
        isUIOptimisation={isDriverAndFoodUIOptimisation()}
      />
    </>
  );
};

export const getDriverOrderCode = (order: FoodOrderInfo) => {
  if (isDriverVN()) {
    return null;
  }
  return order.service_type === FoodOrderServiceType.Food ? order.pickup_seq : order.short_code || order.reference_order_id;
};

export default inject(getChildInject('initStore'))(observer(OrderCardWithButton));
